import { AdminDrawer } from "@/components/admin/AdminDrawer";
import type {
  Product,
  ProductCategory,
  ProductUnit,
} from "@/features/product/types";
import type { ProductRequest } from "@/features/productRequest/types";
import "./ProductDrawers.css";

export type ProductRequestReviewMode = "create" | "map" | "reject";

export type ProductMappingSearch = {
  query: string;
  items: Product[];
  loading: boolean;
  disabled: boolean;
  error: string;
  onRetry: () => void;
  onQueryChange: (query: string) => void;
};

type ProductRequestReviewDrawerProps = {
  open: boolean;
  mode: ProductRequestReviewMode;
  selectedRequest?: ProductRequest;
  mappingSearch: ProductMappingSearch;
  categoryLabels: Record<ProductCategory, string>;
  unitLabels: Record<ProductUnit, string>;
  requestName: string;
  requestCategory: ProductCategory;
  requestUnit: ProductUnit;
  mappedProductId: string;
  rejectionNote: string;
  error: string;
  pending: boolean;
  onRequestNameChange: (value: string) => void;
  onRequestCategoryChange: (value: ProductCategory) => void;
  onRequestUnitChange: (value: ProductUnit) => void;
  onMappedProductIdChange: (value: string) => void;
  onRejectionNoteChange: (value: string) => void;
  onClose: () => void;
  onSubmit: () => void;
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "0.625rem 0.75rem",
  background: "var(--login-bg)",
  border: "1px solid var(--login-border)",
  borderRadius: "4px",
  color: "var(--login-text)",
  fontFamily: "'DM Sans', sans-serif",
  fontSize: "0.9375rem",
  boxSizing: "border-box",
};

const selectStyle: React.CSSProperties = {
  ...inputStyle,
  appearance: "none",
  paddingRight: "2.25rem",
  backgroundImage: `url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23888888' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>")`,
  backgroundRepeat: "no-repeat",
  backgroundPosition: "right 0.75rem center",
  backgroundSize: "0.75rem",
};

const labelStyle: React.CSSProperties = {
  fontFamily: "'Barlow Condensed', sans-serif",
  fontWeight: 600,
  fontSize: "0.75rem",
  letterSpacing: "0.06em",
  color: "var(--login-muted)",
};

const btnPrimary: React.CSSProperties = {
  flex: 1,
  padding: "0.75rem",
  background: "var(--login-accent)",
  border: "none",
  borderRadius: "4px",
  color: "#fff",
  fontFamily: "'Barlow Condensed', sans-serif",
  fontWeight: 700,
  fontSize: "1rem",
  letterSpacing: "0.06em",
};

const btnDanger: React.CSSProperties = {
  flex: 1,
  padding: "0.75rem",
  border: "1px solid var(--login-error, #e05353)",
  borderRadius: "4px",
  color: "var(--login-error, #e05353)",
  fontFamily: "'Barlow Condensed', sans-serif",
  fontWeight: 700,
  fontSize: "1rem",
  letterSpacing: "0.06em",
};

function titleForMode(mode: ProductRequestReviewMode) {
  if (mode === "create") return "Criar Produto da Solicitação";
  if (mode === "map") return "Usar Produto Existente";
  return "Rejeitar Solicitação";
}

export function ProductRequestReviewDrawer({
  open,
  mode,
  selectedRequest,
  mappingSearch,
  categoryLabels,
  unitLabels,
  requestName,
  requestCategory,
  requestUnit,
  mappedProductId,
  rejectionNote,
  error,
  pending,
  onRequestNameChange,
  onRequestCategoryChange,
  onRequestUnitChange,
  onMappedProductIdChange,
  onRejectionNoteChange,
  onClose,
  onSubmit,
}: ProductRequestReviewDrawerProps) {
  return (
    <AdminDrawer
      open={open}
      onClose={onClose}
      onSubmit={onSubmit}
      submitDisabled={pending}
      title={titleForMode(mode)}
      visualTreatment="operational-sheet"
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        {selectedRequest && (
          <div
            style={{
              border: "1px solid var(--login-border)",
              borderRadius: "0.75rem",
              padding: "0.85rem",
              background: "var(--login-surface)",
            }}
          >
            <div
              style={{
                fontFamily: "'Barlow Condensed', sans-serif",
                fontWeight: 700,
                fontSize: "0.95rem",
                letterSpacing: "0.06em",
                color: "var(--login-text)",
              }}
            >
              {selectedRequest.requested_name}
            </div>
            <div
              style={{
                marginTop: "0.25rem",
                fontFamily: "'DM Sans', sans-serif",
                fontSize: "0.875rem",
                color: "var(--login-muted)",
              }}
            >
              {selectedRequest.supplier_name ?? "Fornecedor"}
            </div>
          </div>
        )}

        {mode === "create" && (
          <>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.375rem",
              }}
            >
              <label htmlFor="product-request-review-name" style={labelStyle}>
                Nome
              </label>
              <input
                className="product-drawer-control"
                id="product-request-review-name"
                style={inputStyle}
                value={requestName}
                onInput={(event) =>
                  onRequestNameChange(event.currentTarget.value)
                }
              />
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.375rem",
              }}
            >
              <label
                htmlFor="product-request-review-category"
                style={labelStyle}
              >
                Categoria
              </label>
              <select
                className="product-drawer-control"
                id="product-request-review-category"
                style={selectStyle}
                value={requestCategory}
                onChange={(event) =>
                  onRequestCategoryChange(event.target.value as ProductCategory)
                }
              >
                {(
                  Object.entries(categoryLabels) as [ProductCategory, string][]
                ).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.375rem",
              }}
            >
              <label htmlFor="product-request-review-unit" style={labelStyle}>
                Unidade preferida
              </label>
              <select
                className="product-drawer-control"
                id="product-request-review-unit"
                style={selectStyle}
                value={requestUnit}
                onChange={(event) =>
                  onRequestUnitChange(event.target.value as ProductUnit)
                }
              >
                {(Object.entries(unitLabels) as [ProductUnit, string][]).map(
                  ([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ),
                )}
              </select>
            </div>
          </>
        )}

        {mode === "map" && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "0.625rem",
            }}
          >
            <label
              htmlFor="product-request-review-map-search"
              style={labelStyle}
            >
              Buscar produto existente
            </label>
            <input
              className="product-drawer-control"
              id="product-request-review-map-search"
              type="search"
              value={mappingSearch.query}
              onInput={(event) =>
                mappingSearch.onQueryChange(event.currentTarget.value)
              }
              placeholder="Nome do produto"
              autoComplete="off"
              style={{ ...inputStyle, minHeight: "44px" }}
            />

            {mappingSearch.loading && mappingSearch.items.length === 0 ? (
              <p
                role="status"
                style={{
                  margin: "0.25rem 0",
                  color: "var(--login-muted)",
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: "0.875rem",
                }}
              >
                Buscando produtos…
              </p>
            ) : mappingSearch.items.length > 0 ? (
              <>
                <div
                  role="radiogroup"
                  aria-label="Produtos encontrados"
                  aria-busy={mappingSearch.loading}
                  style={{
                    borderTop: "1px solid var(--login-border)",
                  }}
                >
                  {mappingSearch.items.map((product) => (
                    <label
                      key={product.id}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1.25rem minmax(0, 1fr)",
                        alignItems: "center",
                        columnGap: "0.75rem",
                        minHeight: "44px",
                        padding: "0.625rem 0.25rem",
                        borderBottom: "1px solid var(--login-border)",
                        cursor: "pointer",
                      }}
                    >
                      <input
                        type="radio"
                        name="product-request-review-map"
                        value={product.id}
                        checked={mappedProductId === product.id}
                        disabled={mappingSearch.disabled}
                        onChange={(event) =>
                          onMappedProductIdChange(event.currentTarget.value)
                        }
                        style={{
                          width: "1.125rem",
                          height: "1.125rem",
                          margin: 0,
                          accentColor: "var(--login-accent)",
                        }}
                      />
                      <span style={{ minWidth: 0 }}>
                        <span
                          style={{
                            display: "block",
                            color: "var(--login-text)",
                            fontFamily: "'DM Sans', sans-serif",
                            fontSize: "0.9375rem",
                            fontWeight: 600,
                            lineHeight: 1.35,
                            overflowWrap: "anywhere",
                          }}
                        >
                          {product.name}
                        </span>
                        <span
                          style={{
                            display: "block",
                            marginTop: "0.125rem",
                            color: "var(--login-muted)",
                            fontFamily: "'DM Sans', sans-serif",
                            fontSize: "0.8125rem",
                            lineHeight: 1.35,
                          }}
                        >
                          {categoryLabels[product.category]} ·{" "}
                          {unitLabels[product.default_unit]}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
                {mappingSearch.loading ? (
                  <span
                    role="status"
                    style={{
                      color: "var(--login-muted)",
                      fontFamily: "'DM Sans', sans-serif",
                      fontSize: "0.8125rem",
                    }}
                  >
                    Atualizando resultados…
                  </span>
                ) : null}
              </>
            ) : mappingSearch.error ? null : (
              <p
                style={{
                  margin: "0.25rem 0",
                  color: "var(--login-muted)",
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: "0.875rem",
                }}
              >
                {mappingSearch.query.trim()
                  ? `Nenhum produto encontrado para “${mappingSearch.query.trim()}”.`
                  : "Nenhum produto encontrado."}
              </p>
            )}
            {mappingSearch.error ? (
              <div className="product-mapping-error" role="alert">
                <span>{mappingSearch.error}</span>
                <button
                  className="product-mapping-retry"
                  onClick={mappingSearch.onRetry}
                  type="button"
                >
                  Tentar novamente
                </button>
              </div>
            ) : null}
          </div>
        )}

        {mode === "reject" && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "0.375rem",
            }}
          >
            <label htmlFor="product-request-review-reject" style={labelStyle}>
              Motivo
            </label>
            <textarea
              className="product-drawer-control"
              id="product-request-review-reject"
              style={{
                ...inputStyle,
                minHeight: "7rem",
                resize: "vertical",
              }}
              value={rejectionNote}
              onChange={(event) => onRejectionNoteChange(event.target.value)}
              placeholder="(Opcional)"
            />
          </div>
        )}

        {error && (
          <p
            role="alert"
            style={{
              margin: 0,
              fontFamily: "'DM Sans', sans-serif",
              fontSize: "0.875rem",
              color: "var(--login-error, #e05353)",
            }}
          >
            {error}
          </p>
        )}

        <div style={{ display: "flex", gap: "0.75rem" }}>
          <button
            className="btn-danger product-drawer-action"
            style={btnDanger}
            onClick={onClose}
            disabled={pending}
          >
            Cancelar
          </button>
          <button
            className="btn-primary product-drawer-action"
            style={btnPrimary}
            onClick={onSubmit}
            disabled={pending}
          >
            Confirmar
          </button>
        </div>
      </div>
    </AdminDrawer>
  );
}
